// Comparability shared by detectExceptions and sizeOrderSatisfied, so a flag settles
// exactly when it would not be raised: measurements order only inside a unit family.
import { sizeFamilyOf } from "@CommonUtils/cdtStudio/domain/sizeFamily";
import { isPlaceholderValue } from "../domain/studioTypes";

export interface SizeOrderPlan {
  spine: readonly string[];
  get: (id: string) => { path: readonly (string | null)[] } | undefined;
}

/** The family a size value orders inside; "" for a blank, a "0", or a size BAND. */
export const sizeFamilyKeyOf = (value: string | null | undefined): string => {
  const label = String(value ?? "");
  if (isPlaceholderValue(label)) return "";
  const parsed = sizeFamilyOf(label);
  return parsed.magnitude > 0 ? parsed.family : "";
};

/** Comparable within one unit family, or when neither side is a measurement. */
export const sizePairComparable = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  if (isPlaceholderValue(a) || isPlaceholderValue(b)) return false;
  const left = sizeFamilyKeyOf(a);
  const right = sizeFamilyKeyOf(b);
  if (left && right) return left === right;
  return !left && !right;
};

export const avoidableWithinFamilies = (
  plan: SizeOrderPlan,
  ids: readonly string[],
  sizeIndex: number,
  inversions: (plan: SizeOrderPlan, ids: readonly string[]) => { avoidable: number },
): number => {
  if (sizeIndex < 0) return 0;
  const byFamily = new Map<string, string[]>();
  for (const id of ids) {
    const item = plan.get(id);
    if (!item) continue;
    const family = sizeFamilyKeyOf(item.path[sizeIndex]);
    if (!family) continue;
    byFamily.set(family, [...(byFamily.get(family) ?? []), id]);
  }
  let total = 0;
  byFamily.forEach((familyIds) => {
    if (familyIds.length > 1) total += inversions(plan, familyIds).avoidable;
  });
  return total;
};
